import { Button, Divider, Grid, TextField, Typography } from '@mui/material';
import CurrencySelector from 'components/CurrencySelector';
import Dropzone, { UploadedFile } from 'components/Dropzone';
import ActionSelector from 'components/new/ActionSelector';
import CounterpartySelector from 'components/new/CounterpartySelector';
import Toggle from 'components/new/Toggle';
import VatRateSelector from 'components/new/VatRateSelector';
import { useLayoutContext } from 'contexts/LayoutContext';
import { UploadType } from 'further-types/files';
import { ActionRef } from 'further-types/lp/action';
import {
  IncomeOrExpenseState,
  IncomeOrExpenseType,
} from 'further-types/lp/income-and-expenses';
import { CurrencyCode } from 'further-types/lp/vehicle';
import BasicSelect from 'further-ui/components/BasicSelect';
import FormField from 'further-ui/components/FormField';
import { Column } from 'further-ui/components/Layout';
import NumberInput from 'further-ui/components/NumberInput';
import StaticField from 'further-ui/components/StaticField';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import useFundingSourceOptions from 'hooks/form/useFundingSourceOptions';
import useFileUpload, { UploadFileResult } from 'hooks/ui/useFileUpload';
import { useNotification } from 'hooks/ui/useNotification';
import api from 'lib/trpcClient';
import { FC, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormValues } from '../FormWrapper';
import useSuggestIncomeAndExpense from '../hooks/useSuggestIncomeAndExpense';
import LedgerDateInput from './LedgerDateInput';

const buildActionOptionsName = (action: {
  actionRef: ActionRef;
  asset: {
    name: string;
  };
  ledgerDate: CalendarDay;
}) => {
  switch (action.actionRef) {
    case ActionRef.Investment:
      return `Investment into ${
        action.asset?.name
      } on ${action.ledgerDate.toLabel()}`;
    case ActionRef.Realisation:
      return `Realisation of ${
        action.asset?.name
      } on ${action.ledgerDate.toLabel()}`;
    default:
      return `Unknown action type: ${action.actionRef}`;
  }
};

type Props = {
  id?: string;
  vehicleId: string;
  incomeOrExpense: IncomeOrExpenseType | '';
  transactionCurrency: CurrencyCode;
};

const InputDetailsForm: FC<Props> = ({
  id,
  incomeOrExpense,
  transactionCurrency,
  vehicleId,
}) => {
  const { availableVehicles } = useLayoutContext();
  const { control, setValue, watch, resetField, getValues } =
    useFormContext<FormValues>();
  const { uploadFile } = useFileUpload();
  const { error } = useNotification();
  const [switchedFromAccrual, setSwitchedFromAccrual] = useState(false);

  const { fetchSuggestedIncomeOrExpense, syncSuggestedIncomeOrExpense } =
    useSuggestIncomeAndExpense({
      vehicleId,
    });

  const [contacts] = api.contact.list.useSuspenseQuery();
  const [actions] = api.action.listLinkableActions.useSuspenseQuery({
    vehicleId,
  });
  const [typeOptions] = api.incomeAndExpenses.getTypeOptions.useSuspenseQuery({
    vehicleId,
  });

  const handleFileUpload = async (
    files: Array<UploadedFile>,
    onSuccess: (uploadFileResult: UploadFileResult) => void,
  ) => {
    if (!files?.[0]?.path) return;
    const uploadFileResult = await uploadFile(
      files[0],
      UploadType.LpTransactionAttachments,
    );
    if (uploadFileResult.data) {
      onSuccess(uploadFileResult.data);
    } else {
      error(uploadFileResult.error.message);
    }
  };

  const category = watch('category');

  const onCounterpartyChange = async (counterparty: {
    id?: string;
    name: string;
  }) => {
    const suggestedIncomeOrExpense = await fetchSuggestedIncomeOrExpense(
      counterparty,
    );

    syncSuggestedIncomeOrExpense({
      suggestedIncomeOrExpense: suggestedIncomeOrExpense ?? null,
      counterparty,
      setValue,
    });
  };

  const fundingSourceOptions = useFundingSourceOptions(watch('vehicleId'));
  const actionOptions = actions.map((action) => ({
    id: action.id,
    name: buildActionOptionsName(action),
    date: action.ledgerDate,
    key: action.id,
  }));

  const isIncome = incomeOrExpense === IncomeOrExpenseType.Income;

  const incomeOrExpenseTypeOptions = isIncome
    ? typeOptions.incomeTypeOptions
    : typeOptions.expenseTypeOptions;

  const incomeOrExpenseTypeLabel = isIncome ? 'Income type' : 'Expense type';

  const descriptionLabel = isIncome ? 'Income name' : 'Expense name';

  const incomeOrExpenseState = watch('incomeOrExpenseState');
  const isAccrued = incomeOrExpenseState === IncomeOrExpenseState.Accrued;
  const shouldShowSwitchToPayableButton = !!id && isAccrued;

  const incomeOrExpenseStateLabel =
    incomeOrExpense === 'income' ? 'Income state' : 'Expense state';

  const incomeOrExpenseStateOptions = isIncome
    ? [
        { value: IncomeOrExpenseState.Receivable, label: 'Receivable income' },
        { value: IncomeOrExpenseState.Accrued, label: 'Accrued income' },
      ]
    : [
        { value: IncomeOrExpenseState.Payable, label: 'Payable expense' },
        { value: IncomeOrExpenseState.Accrued, label: 'Accrued expense' },
      ];

  const incomeOrExpenseStateTooltip =
    incomeOrExpense === 'income'
      ? "Use Receivable income when you've issued an invoice and expect to be paid.\n\nUse Accrued income when no invoice has been issued for the income yet."
      : 'Use a Payable expense when you have received an invoice and know the exact amount to pay.\n\nUse an Accrued expense when no invoice has been received for the expense yet.';

  return (
    <Column spacing="lg">
      <FormField size="md">
        <Controller
          name="category"
          control={control}
          render={({ field: { ref, onChange, ...rest } }) => (
            <Toggle
              {...rest}
              options={[
                { value: IncomeOrExpenseType.Income, label: 'Income' },
                { value: IncomeOrExpenseType.Expense, label: 'Expense' },
              ]}
              onChange={(value) => {
                onChange(value);
                getValues().apportionmentPeriods?.forEach((_, index) =>
                  resetField(`apportionmentPeriods.${index}.transactions`),
                );
                resetField('incomeOrExpenseType');
                resetField('incomeOrExpenseState');
              }}
              disabled={!!id}
            />
          )}
        />
      </FormField>
      <FormField size="md" label="Vehicle">
        <BasicSelect
          value={vehicleId}
          onChange={() => {}}
          fullWidth
          options={
            availableVehicles?.map((vehicle) => ({
              value: vehicle._id,
              label: vehicle.vehicleName,
            })) ?? []
          }
          placeholder="Select vehicle"
          disabled={true}
        />
      </FormField>
      <FormField size="md" label="Counterparty">
        <Controller
          name="counterparty"
          control={control}
          render={({
            fieldState: { error },
            field: { onChange, ...field },
          }) => (
            <CounterpartySelector
              contacts={contacts}
              error={error}
              placeholder="Select counterparty"
              onChange={(value) => {
                if (value) {
                  onChange(value);
                  onCounterpartyChange(value);
                }
              }}
              {...field}
            />
          )}
        />
      </FormField>
      <FormField size="md" label={incomeOrExpenseTypeLabel}>
        <Controller
          name="incomeOrExpenseType"
          control={control}
          render={({ fieldState: { error }, field: { onChange, ...rest } }) => (
            <BasicSelect
              fullWidth
              disabled={!incomeOrExpense}
              options={incomeOrExpenseTypeOptions}
              placeholder={`Select ${incomeOrExpenseTypeLabel.toLowerCase()}`}
              error={!!error}
              onChange={(value) => {
                onChange(value);
                getValues().apportionmentPeriods?.forEach((_, index) =>
                  resetField(`apportionmentPeriods.${index}.transactions`),
                );
              }}
              helperText={error?.message}
              {...rest}
            />
          )}
        />
      </FormField>
      <FormField
        size="md"
        label={incomeOrExpenseStateLabel}
        tooltip={incomeOrExpenseStateTooltip}
      >
        {shouldShowSwitchToPayableButton ? (
          <Button
            variant="outlined"
            fullWidth
            onClick={() => {
              setValue(
                'incomeOrExpenseState',
                isIncome
                  ? IncomeOrExpenseState.Receivable
                  : IncomeOrExpenseState.Payable,
              );
              getValues().apportionmentPeriods?.forEach((_, index) =>
                resetField(`apportionmentPeriods.${index}.transactions`),
              );
              setSwitchedFromAccrual(true);
            }}
            sx={{ height: 40 }}
          >
            {isIncome
              ? 'Switch to receivable income'
              : 'Switch to payable expense'}
          </Button>
        ) : (
          <Controller
            name="incomeOrExpenseState"
            control={control}
            render={({
              fieldState: { error },
              field: { onChange, ...rest },
            }) => (
              <BasicSelect
                fullWidth
                disabled={!incomeOrExpense}
                options={incomeOrExpenseStateOptions}
                placeholder={`Select ${incomeOrExpenseStateLabel.toLowerCase()}`}
                error={!!error}
                onChange={(value) => {
                  onChange(value);
                  getValues().apportionmentPeriods?.forEach((_, index) =>
                    resetField(`apportionmentPeriods.${index}.transactions`),
                  );
                  if (value === IncomeOrExpenseState.Accrued) {
                    setValue('vatRate', 0);
                    setValue('fundingSource', undefined);
                    setValue('invoiceFile', null);
                  }
                }}
                helperText={error?.message}
                {...rest}
              />
            )}
          />
        )}
      </FormField>
      <FormField size="md" label="Linked actions">
        <Controller
          name="sourceAllocationActionId"
          control={control}
          render={({ field: { value, onChange } }) => (
            <ActionSelector
              actions={actionOptions}
              placeholder="Select action"
              value={
                value
                  ? actionOptions.find((action) => action.id === value)
                  : null
              }
              onChange={(newValue) => {
                onChange(newValue?.id ?? null);
              }}
            />
          )}
        />
      </FormField>

      {category === IncomeOrExpenseType.Expense && !isAccrued && (
        <FormField
          size="md"
          label="Funding source"
          tooltip="Select whether this action will be paid through general working capital on account or fresh capital that will be part of a future capital call."
        >
          <Controller
            name="fundingSource"
            control={control}
            render={({ fieldState: { error }, field: { ref, ...rest } }) => (
              <BasicSelect
                fullWidth
                error={!!error}
                options={fundingSourceOptions.options}
                placeholder="Select funding source"
                helperText={error?.message?.toString()}
                disabled={
                  fundingSourceOptions.isLoading ||
                  (!!id && !switchedFromAccrual)
                }
                {...rest}
              />
            )}
          />
        </FormField>
      )}

      <FormField
        size="md"
        label="Ledger date"
        tooltip="The date or date range this transaction should be recorded for in the general ledger."
      >
        <LedgerDateInput />
      </FormField>
      <FormField size="md" label="Currency">
        <Controller
          name="currencyCode"
          control={control}
          render={({ fieldState: { error }, field: { onChange, ...rest } }) => (
            <CurrencySelector
              error={error}
              placeholder="Enter currency"
              onChange={(currency) => {
                onChange(currency);
                getValues().apportionmentPeriods?.forEach((_, index) =>
                  resetField(`apportionmentPeriods.${index}.currencyRate`),
                );
              }}
              {...rest}
            />
          )}
        />
      </FormField>

      <FormField size="md" label="Enter amount (excl. sales tax)">
        <Grid size={{ sm: 12, md: 12, lg: 6 }}>
          <Controller
            name="amountExVatTcy"
            control={control}
            render={({
              fieldState: { error },
              field: { ref, onChange, ...rest },
            }) => (
              <NumberInput
                fullWidth
                {...rest}
                onChange={(value) => {
                  onChange(value);
                }}
                decimals={2}
                error={!!error}
                helperText={error?.message?.toString()}
                placeholder="Enter amount"
              />
            )}
          />
        </Grid>
      </FormField>
      {!isAccrued && (
        <FormField size="md" label="Sales tax (e.g. VAT) rate">
          <Controller
            name="vatRate"
            control={control}
            render={({ fieldState: { error }, field }) => (
              <VatRateSelector
                error={error}
                placeholder="Enter VAT rate"
                {...field}
              />
            )}
          />
        </FormField>
      )}
      <StaticField size="md" label="Gross amount (inc. tax)">
        <Controller
          name="amountIncVatTcy"
          control={control}
          render={({ field: { value } }) => (
            <Typography variant="h6">
              {numberToCurrencyString(value, {
                fallback: '-',
                currency: transactionCurrency,
              })}
            </Typography>
          )}
        />
      </StaticField>

      <FormField size="md" label={descriptionLabel}>
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <TextField {...field} fullWidth size="small" />
          )}
        />
      </FormField>
      {!isAccrued && (
        <>
          <Divider />
          <FormField size="md" label="Invoice file (if applicable)">
            <Controller
              name="invoiceFile"
              control={control}
              render={({ field: { ref, onChange, value, ...rest } }) => (
                <Dropzone
                  {...rest}
                  ref={ref}
                  maxFiles={1}
                  defaultAcceptGroup="filesAndImages"
                  onDropFile={async (files) => {
                    await handleFileUpload(files, (uploadFileResult) => {
                      onChange({
                        path: uploadFileResult.filePath,
                        name: uploadFileResult.name,
                        preview: uploadFileResult.url,
                      });
                    });
                  }}
                  files={
                    value
                      ? [
                          {
                            preview: value.preview,
                            fileName: value.name,
                          },
                        ]
                      : []
                  }
                  onRemove={() => {
                    onChange(null);
                  }}
                />
              )}
            />
          </FormField>
        </>
      )}
    </Column>
  );
};

export default InputDetailsForm;
